import { apiService } from '@/services/api/apiService';
import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';

interface AreaManagerDeliveries {
  orderReference: any;
  customerOrderId: any;
  trackings: any;
  userProfileName: any;
  areaManagerCollected: {};
  isCollected: any;
  foodScheduleId: any;
  areaManagerName: any;
}
interface CustomSearchData {
  CustomSearchData: any;
  loading: boolean;
  error: string | null;
}
interface DeliveryStatusData {
  DeliveryStatusData: any;
  loading: boolean;
  error: string | null;
}
interface deliveryTracking {}
interface AreaManagerDeliveriesState {
  data: AreaManagerDeliveries[];
  CustomSearchData: any;
  DeliveryStatusData: any;
  loading: boolean;
  error: string | null;
}

const PORT = import.meta.env.VITE_FOOD_ORDER_PORT;
const URL = import.meta.env.VITE_API_FOOD_ORDER_URL;

export const fetchAreaManagerDeliveries = createAsyncThunk<
  AreaManagerDeliveries[],
  void,
  { rejectValue: string }
>('AreaManagerDeliveries/fetchAreaManagerDeliveries', async (_, { rejectWithValue }) => {
  try {
    const response = await apiService.get<{ data: AreaManagerDeliveries[] }>(
      '/get_all_delivery_tracking',
      PORT,
      URL,
    );
    return response.data;
  } catch (error) {
    return rejectWithValue(error instanceof Error ? error.message : 'An error occurred');
  }
});

export const fetchDeliveryStatus = createAsyncThunk<
  DeliveryStatusData[],
  void,
  { rejectValue: string }
>('AreaManagerDeliveries/fetchDeliveryStatus', async (_, { rejectWithValue }) => {
  try {
    const response = await apiService.get<{ data: DeliveryStatusData[] }>(
      '/get_all_delivery_status',
      PORT,
      URL,
    );
    return response.data;
  } catch (error) {
    return rejectWithValue(error instanceof Error ? error.message : 'An error occurred');
  }
});

export const createDeliveryTracking = createAsyncThunk<
  deliveryTracking,
  any,
  { rejectValue: string }
>('AreaManagerDeliveries/createDeliveryTracking', async (params, { rejectWithValue }) => {
  try {
    const response = await apiService.post<{ data: deliveryTracking }>(
      'create_delivery_tracking',
      params,
      PORT,
      URL,
    );
    return response.data;
  } catch (error) {
    return rejectWithValue(error instanceof Error ? error.message : 'An error occurred');
  }
});
export const customSearchDeliveryTracking = createAsyncThunk<
  CustomSearchData[],
  { deliveryAgentId: any },
  { rejectValue: string }
>('AreaManagerDeliveries/customSearchDeliveryTracking', async (params, { rejectWithValue }) => {
  try {
    const config = { params };
    const response = await apiService.get<{ data: CustomSearchData[] }>(
      '/custom_search_delivery_tracking',

      PORT,
      URL,
      config,
    );
    return response.data;
  } catch (error: any) {
    return rejectWithValue(error?.message || 'Failed to fetch areas covered');
  }
});

export const AreaManagerDeliveriesSlice = createSlice({
  name: 'AreaManagerDeliveries',
  initialState: {
    data: [],
    CustomSearchData: [],
    DeliveryStatusData: [],
    loading: false,
    error: null,
  } as AreaManagerDeliveriesState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchAreaManagerDeliveries.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchAreaManagerDeliveries.fulfilled,
        (state, action: PayloadAction<AreaManagerDeliveries[]>) => {
          state.loading = false;
          state.data = action.payload;
        },
      )
      .addCase(fetchAreaManagerDeliveries.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload ?? 'Unknown error';
      })
      .addCase(fetchDeliveryStatus.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchDeliveryStatus.fulfilled,
        (state, action: PayloadAction<DeliveryStatusData[]>) => {
          state.loading = false;
          state.DeliveryStatusData = action.payload;
        },
      )
      .addCase(fetchDeliveryStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload ?? 'Unknown error';
      })

      .addCase(createDeliveryTracking.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createDeliveryTracking.fulfilled, (state, action: any) => {
        state.loading = false;
        state.data = action.payload;
      })
      .addCase(createDeliveryTracking.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload ?? 'Unknown error';
      })
      .addCase(customSearchDeliveryTracking.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        customSearchDeliveryTracking.fulfilled,
        (state, action: PayloadAction<CustomSearchData[]>) => {
          state.loading = false;
          state.CustomSearchData = action.payload;
        },
      )
      .addCase(customSearchDeliveryTracking.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload ?? 'Unknown error';
      });
  },
});

export default AreaManagerDeliveriesSlice.reducer;
